/**
 * Pure RBAC utility functions — no React, no store access.
 * Extracted from admin-ui unified-auth.ts.
 *
 * Note: ROLE_PAGES mapping stays in each app (app-specific page lists).
 * These functions are the building blocks for page-level access checks.
 */

import type { SystemRole } from './types'

export const isReadOnlyRole = (role: SystemRole): boolean => {
  return role === 'auditor'
}

/**
 * Guest is the only role an external member holds (ADR-20260930): chat app
 * only, missions they are invited to, no organisation-wide views, no
 * creation and no inviting. The services enforce it; this lets a client hide
 * what a Guest cannot use.
 */
export const isGuestRole = (role: SystemRole | string | undefined | null): boolean => {
  return role === 'guest'
}

/**
 * Check if role can change another user's role.
 * Owner/admin/manager can change roles, with restrictions:
 * - Owner cannot change their own role
 * - No one can change another user's owner role
 */
export const canChangeUserRole = (
  currentRole: SystemRole,
  targetRole?: SystemRole,
  isViewingSelf?: boolean,
): boolean => {
  if (currentRole === 'owner' || currentRole === 'admin' || currentRole === 'manager') {
    if (currentRole === 'owner' && isViewingSelf) return false
    if (targetRole === 'owner' && !isViewingSelf) return false
    return true
  }
  return false
}

/**
 * Check if role can delete a user.
 * Owner/admin/manager can delete, with restrictions:
 * - Owner cannot delete themselves
 * - Admin/manager cannot delete owner accounts
 */
export const canDeleteUser = (
  currentRole: SystemRole,
  isViewingSelf?: boolean,
  targetRole?: SystemRole,
): boolean => {
  if (currentRole === 'owner' || currentRole === 'admin' || currentRole === 'manager') {
    if (currentRole === 'owner' && isViewingSelf) return false
    if ((currentRole === 'admin' || currentRole === 'manager') && targetRole === 'owner') return false
    return true
  }
  return false
}

/** Only owner can delete the organization */
export const canDeleteOrganization = (role: SystemRole): boolean => {
  return role === 'owner'
}

export const isViewingOwnProfile = (
  currentUserId: string | undefined,
  viewedUserId: string | undefined,
): boolean => {
  if (!currentUserId || !viewedUserId) return false
  return currentUserId === viewedUserId
}

/**
 * Check if role has governance permission for an action.
 * Pure function — takes role as argument instead of reading from store.
 *
 * - `read`: the governance area; users and managers see only the rules of
 *   their own missions there (agion/Agion#2555).
 * - `catalogue`: the whole rule catalogue, the other Policy Center tabs and
 *   the trust configuration, mirroring the governance-service read gate.
 * - `write`: governance resources (connections, models, tools).
 * - `admin`: policy authorship (rules, the policy designer) and settings.
 */
export const hasGovernancePermission = (
  role: SystemRole,
  action: 'read' | 'catalogue' | 'write' | 'admin' | 'delete' = 'read',
): boolean => {
  switch (action) {
    case 'read':
      return ['owner', 'admin', 'manager', 'user', 'auditor'].includes(role)
    case 'catalogue':
      return ['owner', 'admin', 'auditor'].includes(role)
    case 'write':
      return ['owner', 'admin', 'manager'].includes(role)
    case 'admin':
      return ['owner', 'admin'].includes(role)
    case 'delete':
      return role === 'owner'
    default:
      return false
  }
}

/** Alias — organization permissions follow the same model as governance */
export const hasOrganizationPermission = hasGovernancePermission
